import { useEffect, useState } from 'react';
import { tickerQuotes } from '@/mocks/forex';

type Quote = {
  symbol: string;
  price: number;
  decimals: number;
  delta: number;
};

const SYMBOLS: { symbol: string; decimals: number }[] = [
  { symbol: 'EUR/USD', decimals: 4 },
  { symbol: 'GBP/USD', decimals: 4 },
  { symbol: 'AUD/USD', decimals: 4 },
  { symbol: 'USD/JPY', decimals: 2 },
  { symbol: 'USD/CAD', decimals: 4 },
  { symbol: 'XAU/USD', decimals: 1 },
];

const API_KEY = import.meta.env.VITE_PUBLIC_TWELVEDATA_API_KEY as string | undefined;
// 12 分钟刷新一次：6 个品种 × 120 次/天 = 720 次/天，控制在免费额度（约 800 次/天）内
const REFRESH_MS = 720000;

async function fetchQuote(symbol: string, decimals: number): Promise<Quote> {
  const url = `https://api.twelvedata.com/quote?symbol=${encodeURIComponent(symbol)}&apikey=${API_KEY}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('quote request failed');
  const data = await res.json();
  const price = Number.parseFloat(data.close ?? data.price ?? '0') || 0;
  const delta = Number.parseFloat(data.change ?? '0') || 0;
  return { symbol, price, decimals, delta };
}

export default function LiveTicker() {
  const [quotes, setQuotes] = useState<Quote[]>(() =>
    SYMBOLS.map((s) => {
      const mock = tickerQuotes.find((q) => q.symbol === s.symbol);
      return { ...s, price: mock?.price ?? 0, delta: mock?.delta ?? 0 };
    }),
  );
  const [ready, setReady] = useState(!API_KEY);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!API_KEY) return;
      try {
        const results = await Promise.all(
          SYMBOLS.map((s) => fetchQuote(s.symbol, s.decimals)),
        );
        if (!cancelled) {
          setQuotes(results);
          setReady(true);
        }
      } catch {
        // 请求失败时保留上一次的真实报价，首次失败则继续显示兜底数据
      }
    };

    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const doubled = [...quotes, ...quotes];

  return (
    <div className="relative overflow-hidden border-b border-violet-100">
      <div className="flex w-max animate-ticker items-center gap-7 px-5 py-2.5">
        {doubled.map((q, i) => {
          const up = q.delta >= 0;
          const priceStr = ready ? q.price.toFixed(q.decimals) : '…';
          return (
            <div key={`${q.symbol}-${i}`} className="flex items-center gap-1.5 whitespace-nowrap">
              <span className="font-mono text-[11px] font-medium text-slate-400">{q.symbol}</span>
              <span
                key={priceStr}
                className="price-pop font-mono text-[12px] font-semibold tabular-nums"
                style={{ color: !ready ? '#94a3b8' : up ? '#059669' : '#e11d48' }}
              >
                {priceStr}
              </span>
              {ready && (
                <i
                  className={`${up ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'} text-[10px] ${
                    up ? 'text-emerald-500' : 'text-rose-500'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}